import axios from "axios";
import queryString from "query-string";
import { navigate } from "gatsby";
import { NOT_DASHBOARD_ROUTES } from "../commons/enum";
import { getcompanyDomain, getCurrentPath } from "../utils/get-params-value";
// eslint-disable-next-line import/no-cycle
import { logout } from "../utils/auth";

export const getHostName = () => {
  if (process.env.GATSBY_APP_HOST) {
    return process.env.GATSBY_APP_HOST;
  }
  const isBrowser = () => typeof window !== "undefined";
  if (isBrowser()) {
    const { hostname } = window.location;
    const splittedHostname = hostname.split(".");
    if (splittedHostname.includes("staging")) {
      const index = splittedHostname.lastIndexOf("staging");
      return splittedHostname.slice(index).join(".");
    }
    if (splittedHostname.includes("app")) {
      const index = splittedHostname.lastIndexOf("app");
      return splittedHostname.slice(index + 1).join(".");
    }
    if (splittedHostname.includes("backoffice")) {
      const index = splittedHostname.lastIndexOf("backoffice");
      return splittedHostname.slice(index + 1).join(".");
    }
    return window.location.hostname;
  }
  return "";
};

const isDevelopmentEnv =
  process.env.ACTIVE_ENV === "development" || process.env.NODE_ENV === "development";

export const getAppUrl = (_domain) => {
  let domain = _domain;
  if (!domain) {
    domain = getcompanyDomain();
  }
  return `${
    isDevelopmentEnv ? "http" : "https"
  }://${domain ? `${domain}.` : ""}app.${getHostName()}${isDevelopmentEnv ? ":8001" : ""}`;
};

export const getRootUrl = () => {
  const domain = getcompanyDomain();
  return `${isDevelopmentEnv ? "http" : "https"}://${domain}.${getHostName()}${
    isDevelopmentEnv ? ":8001" : ""
  }`;
};

export const getAppAPIUrl = () => {
  if (process.env.GATSBY_APP_HOST) {
    // In development, use local proxy to avoid third-party cookie blocking
    if (isDevelopmentEnv) {
      return "/api";
    }
    return `https://api.${process.env.GATSBY_APP_HOST}/api`;
  }
  const URL = `${isDevelopmentEnv ? "http" : "https"}://api.${getHostName()}${
    isDevelopmentEnv ? ":3000" : ""
  }/api`;
  return URL;
};

export const getBackofficeUrlOfDomain = (_domain) => {
  let domain = _domain;
  if (!domain) {
    domain = getcompanyDomain();
  }
  if (domain === "backoffice") {
    return `${
      isDevelopmentEnv ? "http" : "https"
    }://${domain}.${getHostName()}${isDevelopmentEnv ? ":8000" : ""}`;
  }
  return `${
    isDevelopmentEnv ? "http" : "https"
  }://${domain}.backoffice.${getHostName()}${isDevelopmentEnv ? ":8000" : ""}`;
};

export const getAgentPlatformUrl = () => {
  if (process.env.GATSBY_AGENT_API_PLATFORM_URL) {
    return process.env.GATSBY_AGENT_API_PLATFORM_URL;
  }
  // Fallback: derive from hostname pattern
  const hostname = getHostName();
  return `${isDevelopmentEnv ? "http" : "https"}://agent.${hostname}${isDevelopmentEnv ? ":3030" : ""}`;
};

export const getAgentServiceUrl = () => {
  if (process.env.GATSBY_AGENT_SERVICE_API_PLATFORM_URL) {
    return process.env.GATSBY_AGENT_SERVICE_API_PLATFORM_URL;
  }
  // Fallback: if not configured, reuse agent platform URL so it still works in dev
  return getAgentPlatformUrl();
};

export const checkIsPrivateRoute = () => {
  const pathname = getCurrentPath();
  return NOT_DASHBOARD_ROUTES.every((item) => pathname.indexOf(item) < 0);
};

const axiosClient = axios.create({
  baseURL: getAppAPIUrl(),
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true,
  paramsSerializer: (params) => queryString.stringify(params),
});

axiosClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  const { method } = config;
  if (method !== "get") {
    axiosClient.defaults.headers.common["csrf-token"] = token;
  }
  return config;
});

axiosClient.interceptors.response.use(
  (response) => {
    if (response && response.data) {
      return response.data;
    }
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const companyStatus = error?.response?.data?.companyStatus;
    const message = error?.response?.data?.message;
    if (status === 403 && message === "Company is not approved for access") {
      if (companyStatus === "AWAITING_APPROVAL") {
        logout(() => navigate("/waiting-for-approval"));
      } else if (companyStatus === "DEACTIVATED" || companyStatus === "PAYMENT_OVERDUE") {
        logout(() => navigate(`/forbidden?companyStatus=${companyStatus}`));
      } else {
        logout(() => navigate("/forbidden"));
      }
    }
    return Promise.reject(error);
  }
);
export default axiosClient;
